/**
 * Football-rules helpers (owner: rules team). Pure, allocation-free, deterministic. Other sim modules may import
 * the read-only helpers below (`carrierAttackDir`, `offenseCatchIsLegal`, …); nothing here mutates state.
 *
 * ══ RULES ⇄ OTHER MODULES: THE STATE-FIELD PROTOCOL ═══════════════════════════════════════════════════════
 * The rules (steps 2, 3, 8, 9 of `step.ts`) coordinate with the other modules ONLY through SimState fields, as
 * follows (events are never read back: the sink may be null). The one exception is the contact team's shared fx
 * helpers (`contact/fx.ts`): the rules use `addHitStop` for the touchdown freeze (so a catch in the end zone folds
 * into ONE HIT_STOP per (tick, actor)) and `fakeSuccess` for the hard-count bait, exactly like every other bait.
 *
 * RULES OWN (nobody else writes these):
 *  - rules.* (phase, phaseFrame, down/distance/LOS/first-down line, play/rush clocks, playCall, offenseCrossed,
 *    score, conversion, overtime, afterDead, lastResult, winner, game clock) — EXCEPT `rules.passThrown`, which
 *    the controller sets on the throw — plus the rules-only bookkeeping fields (prevBallState, prevTouchedMask,
 *    baitsCounted, yardPower, completion, catchX).
 *  - players[i].crossedLos, playBestX (after the snap), hardCountsUsed.
 *  - ALL power GAINS (`players[i].power` only ever goes UP in rules; specials only spend) and the POWER_GAIN /
 *    POWER_FULL events (one POWER_GAIN per player per tick, `reasons` = PowerReason bits).
 *  - The stats statYards, statPassYards, statTackles, statCatches, statInterceptions, statSacks, statTouchdowns.
 *  - Events: DRIVE_START, DOWN_START, SNAP, HARD_COUNT, OFFSIDE, RUSH_GO, PLAY_CLOCK_WARNING, INCOMPLETE,
 *    PLAY_END, FIRST_DOWN, BIG_PLAY, TOUCHDOWN, FIELD_GOAL_GOOD, FIELD_GOAL_MISS, SAFETY, SACK,
 *    TURNOVER_ON_DOWNS, CONVERSION_START/CHOICE/RESULT, OVERTIME_START, GAME_OVER, CELEBRATE, POWER_GAIN,
 *    POWER_FULL, SPECIAL_END{consumed:false} at PLAY_END, HIT_STOP{cause:'touchdown'} (actor = scorer, via
 *    addHitStop), and FAKE_SUCCESS for the hard-count bait (offside, via fakeSuccess).
 *  - Presses: in PRE_SNAP the rules CONSUME the offense's buffered Action (snap, once
 *    RULES.preSnapMinFrames have passed; ▼ held at the snap = FG_TOSS) and buffered ▲ (hard count: sets
 *    p.state = HARD_COUNT with stateFrame 0, feintKind/feintTick; max HARD_COUNTS_PER_PLAY). In CONVERSION they
 *    consume the offense's Action (1-pt toss) and ▲ (2-pt play). Controllers must not use those presses there.
 *  - Scorer's p.state = CELEBRATE (stateFrame 0) on a TD / made FG; the ball becomes DEAD at every play end.
 *
 * WHAT THE RULES READ FROM THE OTHER MODULES (contract for controller / ball / contact / specials):
 *  - TACKLE: the contact module sets the carrier's `state = PlayerState.TACKLED` (ball either stays HELD by the
 *    carrier, or `ball.state = DEAD` with `ball.holder` kept = carrier). Spot = the carrier's head x. The other
 *    player is credited with the tackle (power + statTackles). Ball carried over the goal line first = TD wins.
 *    The next LOS is that spot, but never closer than RULES.minLosYardsToGoal to the attacked goal (`spotLos`;
 *    the offside penalty stops there too), so the goal line cannot be broken on a tick without contacts.
 *  - CATCH / INTERCEPTION: `ball.state` AIR → HELD between two rulesPost calls, `ball.holder` = catcher. An
 *    OFFENSE catch is legal only if `offenseCatchIsLegal` (ball AND head centre beyond the LOS, not beyond the end
 *    line); otherwise the rules end the play INCOMPLETE on that tick. A DEFENSE catch is an interception: the
 *    return stays live (the drive is over whatever happens; a TD over the other goal line = pick-six; on a
 *    conversion the play ends at once, conversion failed). An interception during the RUSH_COUNT ends the count
 *    at once (RUSH_GO{reason:'turnover'}): the returner may cross the LOS and contacts resolve.
 *  - SWAT: a new bit in `ball.touchedMask` (bit i = player i) for a player other than `ball.thrownBy` while the
 *    ball is still loose (not caught by him) → +POWER.swat for him.
 *  - FLIGHT OVER: an AIR ball touching the ground → the ball module sets `ball.state = DEAD`, `holder = -1`, and
 *    leaves `fgAttempt`, `forwardPass`, `thrownBy`, `releaseX` untouched on that tick. Rules classify it:
 *    `fgAttempt` → FIELD_GOAL_MISS ('post' if `ball.bounces > 0`, i.e. it hit a post — the ball module
 *    increments `ball.bounces` on every post hit — else 'short'); otherwise INCOMPLETE. (A LOOSE ball is treated
 *    the same way; a NORMAL flight past either end line is out of bounds = INCOMPLETE at once.) The ball module
 *    must NOT emit INCOMPLETE itself (the rules do, once per play).
 *  - FIELD-GOAL WINDOW: while `ball.state === AIR && ball.fgAttempt`, the rules check every tick whether the ball
 *    centre is past the end-line plane (`endLineFor(attackDir)`). The crossing height is back-interpolated along
 *    (vx, vy) (`fgCrossingY`): strictly between GOALPOST_CROSSBAR_Y and GOALPOST_UPRIGHT_TOP_Y = GOOD, at/above
 *    the top = MISS 'over', at/below the crossbar = MISS 'wide'. The rules then set `ball.state = DEAD` (velocity
 *    kept so the render can let it sail on). Post collisions themselves (DOINK, POST_HIT) belong to the ball
 *    module.
 *  - THE THROW (controller/ball): sets `rules.passThrown = true`, `ball.forwardPass` (= isQbMode at release),
 *    `thrownBy`, `releaseX`, and `fgAttempt = forwardPass && playCall ∈ {FG_TOSS, PAT_TOSS}`; any catch clears
 *    `fgAttempt`.
 *  - BAITS: whoever detects a bait (contact module, controller, specials) emits FAKE_SUCCESS and increments the
 *    faker's `statBaits`; the rules convert every new bait into +POWER.bait on that tick (`baitsCounted`).
 *    NOBODY but the rules adds power.
 *  - BLITZ: at the snap the rules set `rushSkipped = true`, `rushCount = SPECIALS_TUNING.blitz.rushFrames` when
 *    the defense's special is ARMED Blitz, and emit SNAP{blitz: true}. The specials module (same tick, step 4)
 *    turns ARMED → TELEGRAPH, spends the power and emits SPECIAL_TELEGRAPH. RUSH_GO{reason:'blitz'} comes from
 *    the rules when the shortened count runs out. An offside replay before the snap lets lineUpForSnap reset the
 *    arm (no cost).
 *  - SPECIALS AT PLAY END: a special still TELEGRAPH/ACTIVE gets SPECIAL_END{consumed:false} and is set SPENT
 *    (timer 0, decoy removed) by the rules. Specials must not activate in DEAD / CONVERSION / GAME_OVER.
 *  - PHASES the other modules key off: PRE_SNAP (the FRONT of the offense — head centre and the ball in its
 *    glove, `offenseFrontPastLos` — clamped behind the LOS by integrate; the rules re-clamp after the ball module
 *    as a safety net; the DEFENDER is never clamped: crossing is OFFSIDE), RUSH_COUNT (`mayCrossLos`; it ends
 *    early when that front or a forward pass crosses the LOS, or on an interception; OFFSIDE is called only while
 *    the offense holds the ball or its pass is in the air),
 *    LIVE, DEAD / CONVERSION / GAME_OVER (no player actions; a DEAD ball with holder -1 keeps simple ballistic
 *    motion, one with a holder stays in that glove). Stamina regen during play belongs to the controller;
 *    lineUpForSnap refills it between plays.
 * ═════════════════════════════════════════════════════════════════════════════════════════════════════════
 */
import {
  DOWNS_PER_SERIES,
  GOALPOST_CROSSBAR_Y,
  GOALPOST_UPRIGHT_TOP_Y,
  MatchFormat,
  OFFSIDE_PENALTY_YARDS,
  POINTS_SAFETY,
  POINTS_TOUCHDOWN,
  POINTS_TWO_POINT,
  RULES,
  endLineFor,
  goalLineFor,
  yardsToGoal,
} from '@hf/shared';
import { BallState } from '../enums';
import type { SimState } from '../state';

/** Direction player `p` is running toward to score: the offense attacks `attackDir`, an interceptor the other way. */
export const carrierAttackDir = (s: SimState, p: number): number =>
  p === s.rules.offense ? s.rules.attackDir : -s.rules.attackDir;

/** Signed yards of `x` past the LOS in the offense's attack direction. */
export const pastLosX = (s: SimState, x: number): number => (x - s.rules.los) * s.rules.attackDir;

/**
 * An offense catch counts only if the ball AND the catcher's head centre are beyond the LOS (no self-lob cheese)
 * and neither is beyond the attacked end line (out of the back of the end zone).
 */
export const offenseCatchIsLegal = (s: SimState, p: number): boolean => {
  const r = s.rules;
  const pl = s.players[p as 0 | 1];
  const los = r.los;
  const dir = r.attackDir;
  if ((s.ball.x - los) * dir <= 0 || (pl.x - los) * dir <= 0) return false;
  const end = endLineFor(r.attackDir);
  return (end - s.ball.x) * r.attackDir >= 0 && (end - pl.x) * r.attackDir >= 0;
};

/** Has `x` reached/broken the goal-line plane that a runner going `dir` attacks? */
export const inEndZone = (x: number, dir: number): boolean => yardsToGoal(x, dir) <= 0;

/**
 * The x at which a ball dead at `x` is spotted for the next snap of an offense attacking `attackDir`: never closer
 * than RULES.minLosYardsToGoal to the attacked goal line (so the goal line cannot be broken before contacts run).
 */
export const spotLos = (x: number, attackDir: number): number => {
  const limit = goalLineFor(attackDir) - attackDir * RULES.minLosYardsToGoal;
  return attackDir > 0 ? Math.min(x, limit) : Math.max(x, limit);
};

/**
 * Offside penalty: OFFSIDE_PENALTY_YARDS or half the distance to the goal, whichever is smaller — and never past
 * the RULES.minLosYardsToGoal spot (0 once the LOS is already there: the down is simply replayed).
 */
export const offsidePenaltyYards = (los: number, attackDir: number): number => {
  const toGoal = yardsToGoal(los, attackDir);
  const room = toGoal - RULES.minLosYardsToGoal;
  return Math.max(0, Math.min(OFFSIDE_PENALTY_YARDS, toGoal / 2, room));
};

/**
 * How far the FRONT of the offense is past the LOS: its head centre, or the ball in its glove if that is further
 * (the held ball rides up to GLOVE.maxReach ahead of the head). Used for the pre-snap clamp and the rush count's
 * "offense crossed" test, so the ball can never sit across the line while the play is still un-contactable.
 */
export const offenseFrontPastLos = (s: SimState): number => {
  const r = s.rules;
  const head = pastLosX(s, s.players[r.offense as 0 | 1].x);
  const b = s.ball;
  if (b.state !== BallState.HELD || b.holder !== r.offense) return head;
  return Math.max(head, pastLosX(s, b.x));
};

/** `offenseFrontPastLos(s) > 0` without returning a double (hot path: called every rush-count tick). */
export const offenseFrontCrossed = (s: SimState): boolean => {
  const r = s.rules;
  if ((s.players[r.offense as 0 | 1].x - r.los) * r.attackDir > 0) return true;
  const b = s.ball;
  return b.state === BallState.HELD && b.holder === r.offense && (b.x - r.los) * r.attackDir > 0;
};

/**
 * Height at which a ball now past the end-line plane crossed it, back-interpolated along its velocity over the
 * last tick. `past` = how far beyond the plane the ball is (≥ 0). A ball with no horizontal speed crossed at y.
 */
export const fgCrossingY = (bx: number, by: number, vx: number, vy: number, attackDir: number): number => {
  const past = (bx - endLineFor(attackDir)) * attackDir;
  const speed = vx * attackDir;
  if (speed <= 1e-6 || past <= 0) return by;
  return by - vy * (past / speed);
};

export const FgVerdict = { GOOD: 0, OVER: 1, WIDE: 2 } as const;
export type FgVerdict = (typeof FgVerdict)[keyof typeof FgVerdict];

/** GOOD strictly between the crossbar and the upright tops; 'over' at/above the tops; 'wide' at/below the bar. */
export const fgVerdict = (crossY: number): FgVerdict =>
  crossY >= GOALPOST_UPRIGHT_TOP_Y
    ? FgVerdict.OVER
    : crossY <= GOALPOST_CROSSBAR_Y
      ? FgVerdict.WIDE
      : FgVerdict.GOOD;

/** Most points one possession can add for its offense (TD + 2-pt). */
export const MAX_OFFENSE_POINTS_PER_POSSESSION = POINTS_TOUCHDOWN + POINTS_TWO_POINT;
/** Most points the DEFENSE can score during one opponent possession (pick-six; a safety is less). */
export const MAX_DEFENSE_POINTS_PER_POSSESSION =
  POINTS_TOUCHDOWN > POINTS_SAFETY ? POINTS_TOUCHDOWN : POINTS_SAFETY;

/** Who has the ball first in overtime round `round` (1-based): firstPossession on odd rounds, the other on even. */
export const otFirstPossession = (s: SimState, round: number): number =>
  (round & 1) === 1 ? s.config.firstPossession : 1 - s.config.firstPossession;

/**
 * Possessions each player still gets AFTER the current one ends (Drives regulation: drivesPerPlayer minus drives
 * started; overtime in any format: what is left of the current round). Writes into `out[0..1]` and returns true;
 * Classic regulation is clock-bound (returns false, `out` untouched: nothing is decided early) until the second
 * half's clock reads 0:00 (no possessions left: the try after a last-second TD can be a walk-off).
 */
export const remainingPossessions = (s: SimState, out: number[]): boolean => {
  const r = s.rules;
  if (r.overtime) {
    const first = otFirstPossession(s, r.otRound);
    out[0] = 0;
    out[1] = 0;
    if (r.offense === first) out[1 - first] = 1;
    return true;
  }
  if (r.format !== MatchFormat.DRIVES) {
    // Classic regulation is clock-bound: only the final whistle of the second half decides anything early.
    if (r.half !== 2 || r.gameClock > 0) return false;
    out[0] = 0;
    out[1] = 0;
    return true;
  }
  out[0] = Math.max(0, r.drivesPerPlayer - (r.drivesStarted[0] ?? 0));
  out[1] = Math.max(0, r.drivesPerPlayer - (r.drivesStarted[1] ?? 0));
  return true;
};

/**
 * The winner if the result can no longer change, else -1. The trailing player can at best score a TD + 2-pt on
 * each of his remaining possessions and a pick-six on each of the leader's; a lead larger than that is final.
 * With no possessions left, any lead is final (a tie is not: overtime).
 */
export const decidedWinner = (score0: number, score1: number, rem0: number, rem1: number): number => {
  if (score0 === score1) return -1;
  const leader = score0 > score1 ? 0 : 1;
  const lead = leader === 0 ? score0 - score1 : score1 - score0;
  const remTrailer = leader === 0 ? rem1 : rem0;
  const remLeader = leader === 0 ? rem0 : rem1;
  const best = remTrailer * MAX_OFFENSE_POINTS_PER_POSSESSION + remLeader * MAX_DEFENSE_POINTS_PER_POSSESSION;
  return lead > best ? leader : -1;
};

/** Classic format, regulation (not overtime): game clock, halves, kickoffs, punts, field position. */
export const classicRegulation = (s: SimState): boolean =>
  s.rules.format === MatchFormat.CLASSIC && !s.rules.overtime;

/** The kick's receiver on a kick play (the player not kicking = not `rules.offense`). */
export const receiverOf = (s: SimState): number => 1 - s.rules.offense;

/** Is the play on the last down of the series? */
export const isFourthDown = (s: SimState): boolean => s.rules.down >= DOWNS_PER_SERIES;

/** Goal line x the runner going `dir` attacks. */
export const goalFor = (dir: number): number => goalLineFor(dir);
